import type { CSSProperties } from 'react';
import {
  CetItemStatus,
  getSectionsTabScoreBarColor,
  roundScore,
} from '@/lib/cetReadinessData';
import { cn } from '@/lib/utils';

export const GAUGE_TRACK = '#f3f4f6';
export const GAUGE_ACCENT = 'var(--aralin-green)';
export const GAUGE_SIZE = 180;
export const STREAM_PROGRESS_SEGMENTS = 16;

export const readinessGaugeContainer: CSSProperties = {
  width: GAUGE_SIZE,
  height: GAUGE_SIZE,
};

export function streamProgressBarWidth(percent: number): CSSProperties {
  const clamped = Math.min(100, Math.max(0, percent));
  return { width: `${clamped}%` };
}

export function mockExamScoreBar(score: number): CSSProperties {
  return {
    width: `${score}%`,
    backgroundColor: getSectionsTabScoreBarColor(score),
  };
}

export const cetStudentPageLayout = {
  shell: 'flex h-screen overflow-hidden',
  main: 'flex-1 overflow-y-auto bg-aralin-bg transition-all duration-300 px-6 py-6',
  content: 'w-full max-w-full px-2 sm:px-4 pb-12',
  error: 'rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700',
} as const;

export const cetLearningStream = {
  header: 'mb-8',
  title: 'text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight',
  subtitle: 'text-gray-500 mt-2',
  streamsList: 'space-y-5',
  statusCell: {
    base: 'text-xs font-semibold tracking-wide',
    completed: 'text-aralin-green-dark',
    in_progress: 'text-aralin-green',
    not_started: 'text-gray-400',
  },
  progressBar: {
    container: 'flex items-center gap-3',
    label: 'text-[10px] font-medium tracking-wider text-gray-400 uppercase',
    track: 'w-28 h-2 rounded-full bg-gray-200 overflow-hidden',
    fill: 'h-full rounded-full bg-aralin-green transition-all duration-300',
  },
  progressSection: 'flex items-center gap-4 shrink-0 ml-auto',
  moduleTable: {
    empty: 'px-5 py-8 text-center text-sm text-gray-500',
    wrapper: 'overflow-x-auto',
    table: 'w-full min-w-[640px]',
    headRow: 'bg-sky-50/80',
    headCell:
      'px-5 py-3 text-left text-[10px] font-semibold tracking-wider text-gray-500 uppercase',
    headCellRight:
      'px-5 py-3 text-right text-[10px] font-semibold tracking-wider text-gray-500 uppercase',
    bodyRow: 'border-t border-gray-100',
    bodyCell: 'px-5 py-4 text-sm text-gray-900',
    bodyCellMuted: 'px-5 py-4 text-sm text-gray-500',
    bodyCellMutedNumeric: 'px-5 py-4 text-sm text-gray-500 tabular-nums',
    bodyCellAction: 'px-5 py-4 text-right',
    viewButton:
      'h-8 px-5 text-xs font-semibold tracking-wide uppercase bg-[#1e3a5f] hover:bg-[#16304f] text-white rounded-md',
  },
  streamCard: {
    article: 'rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden',
    header: 'flex items-center gap-4 px-5 py-4',
    toggle:
      'flex flex-1 items-center gap-4 min-w-0 text-left hover:bg-gray-50/50 transition-colors rounded-md -m-1 p-1',
    badge:
      'inline-flex shrink-0 items-center rounded-full bg-aralin-green/10 px-2.5 py-1 text-[10px] font-medium tracking-wider text-aralin-green uppercase',
    title: 'flex-1 min-w-0 text-base font-semibold text-gray-900 truncate',
    chevron: 'h-4 w-4 text-gray-400 shrink-0',
    badgeAction:
      'h-8 shrink-0 px-3.5 text-[11px] font-semibold uppercase tracking-wider gap-1.5 whitespace-nowrap',
    badgeActionIcon: 'h-3.5 w-3.5 shrink-0',
    badgeLockedButton:
      'inline-flex shrink-0 items-center gap-1.5 h-8 px-3.5 rounded-md border border-gray-200 bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer whitespace-nowrap',
    badgeLockedIcon: 'h-3.5 w-3.5',
  },
} as const;

export function cetLearningStreamStatusCellClass(status: CetItemStatus): string {
  return cn(
    cetLearningStream.statusCell.base,
    status === 'completed' && cetLearningStream.statusCell.completed,
    status === 'in_progress' && cetLearningStream.statusCell.in_progress,
    status === 'not_started' && cetLearningStream.statusCell.not_started
  );
}

export const cetReadiness = {
  page: {
    header: 'mb-6',
    title: 'text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight',
    subtitle: 'text-gray-500 mt-1',
    grid: 'grid grid-cols-1 lg:grid-cols-4 gap-5 auto-rows-min',
    mockExamsColumn: 'lg:col-span-2 lg:row-span-2',
    whatsNextColumn: 'lg:col-span-2',
  },
  gauge: {
    container: 'relative shrink-0 flex items-center justify-center',
    overlay:
      'absolute inset-0 flex flex-col items-center justify-center pointer-events-none',
    score: 'text-3xl font-bold text-gray-900 tabular-nums',
    label: 'text-xs font-medium text-gray-400 mt-0.5',
    wrapper: 'rounded-2xl bg-gray-50 p-4',
    column: 'flex justify-center lg:justify-start min-w-0',
  },
  streamProgressBar: {
    container: 'flex gap-0.5 items-center flex-1 min-w-0 max-w-[160px]',
    segment: 'flex-1 min-w-[4px] max-w-[8px] h-5 rounded-full',
    segmentFilled: 'bg-aralin-green/80',
    segmentEmpty: 'bg-gray-200',
  },
  badgeModal: {
    overlay:
      'fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4',
    content: 'bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full',
    header: 'flex items-start justify-between mb-4',
    title: 'text-lg font-semibold text-gray-900',
    statusBase: 'text-sm mt-0.5',
    statusEarned: 'text-aralin-green font-medium',
    statusLocked: 'text-gray-500',
    closeButton:
      'rounded-full p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors',
    closeIcon: 'h-5 w-5',
    imageSection: 'flex flex-col items-center mb-4 bg-transparent',
    imageWrapper: 'relative w-[180px] h-[220px] bg-transparent',
    imageBase: 'object-contain',
    imageLocked: 'grayscale opacity-50',
    requirement: 'text-sm text-gray-600 text-center',
    awardDate: 'text-sm text-gray-500 text-center mt-3',
    closeAction: 'w-full mt-4',
  },
  badgeShield: {
    base: [
      'group relative w-[56px] h-[70px] sm:w-[68px] sm:h-[84px] flex-shrink-0 p-0 border-0 bg-transparent cursor-pointer',
      'transition-all duration-300 ease-out',
      'hover:-translate-y-1',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-900/25 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-50',
    ],
    earned: 'hover:drop-shadow-[0_6px_14px_rgba(0,51,102,0.15)]',
    locked: 'hover:opacity-80',
    imageBase: 'object-contain transition-all duration-300 ease-out',
    imageEarned: 'group-hover:brightness-[1.03]',
    imageLocked: 'grayscale opacity-45 group-hover:grayscale-[0.6] group-hover:opacity-55',
  },
  heroBadges: {
    container: [
      'rounded-2xl bg-gray-50 p-4 sm:p-5 w-full max-w-full min-w-0 box-border',
      'flex flex-col gap-4',
      '@min-[768px]/badges:flex-row @min-[768px]/badges:items-center @min-[768px]/badges:gap-5',
    ].join(' '),
    left: [
      'flex items-start gap-3 w-full min-w-0',
      '@min-[768px]/badges:flex-shrink-0 @min-[768px]/badges:w-auto',
    ].join(' '),
    textBlock: 'min-w-0 flex-1',
    awardIcon: 'h-8 w-8 text-cyan-900 flex-shrink-0 mt-0.5',
    countBadge:
      'inline-flex whitespace-nowrap px-2.5 py-1 rounded-full bg-sky-100 text-cyan-900 text-xs font-bold tabular-nums',
    countText: 'text-xs text-gray-500 mt-1.5 leading-relaxed',
    divider:
      'hidden @min-[768px]/badges:block w-px self-stretch bg-gray-200 flex-shrink-0 mx-0.5',
    row: [
      'flex flex-wrap justify-center gap-2 w-full min-w-0 pt-3 border-t border-gray-200',
      '@min-[768px]/badges:justify-end @min-[768px]/badges:flex-1 @min-[768px]/badges:min-w-0',
      '@min-[768px]/badges:pt-0 @min-[768px]/badges:border-t-0',
    ].join(' '),
  },
  heroCard: {
    section: 'rounded-2xl bg-white shadow-sm p-6 sm:p-8 mb-6',
    grid: [
      'grid gap-6 sm:gap-8 items-stretch',
      'grid-cols-1',
      'lg:grid-cols-[auto_minmax(0,1fr)] lg:items-center',
      'xl:grid-cols-[auto_minmax(0,1fr)_minmax(0,32rem)] xl:items-center',
    ].join(' '),
    content: 'min-w-0',
    headline: 'text-xl sm:text-2xl font-semibold text-gray-900 mb-1',
    subtitle: 'text-sm text-gray-500 mb-6',
    streams: 'space-y-4',
    streamRow: 'flex items-center gap-2 sm:gap-3 min-w-0',
    streamIconDone: 'h-5 w-5 text-aralin-green flex-shrink-0',
    streamIconPending: 'h-5 w-5 text-gray-300 flex-shrink-0',
    streamLabel: 'text-sm text-gray-800 w-[88px] sm:w-[120px] flex-shrink-0',
    streamCount: 'text-sm text-gray-500 tabular-nums w-10 text-right flex-shrink-0',
    badgesColumn: [
      '@container/badges flex w-full min-w-0 justify-center',
      'lg:col-span-2',
      'xl:col-span-1 xl:justify-end',
    ].join(' '),
  },
  streamCardLink:
    'inline-flex items-center gap-1 text-sm font-medium text-aralin-green hover:underline mt-auto pt-5',
  streamCardLinkIcon: 'h-4 w-4',
  mockExamStatus: {
    completed: 'h-4 w-4 text-aralin-green flex-shrink-0',
    inProgress: 'w-4 text-center text-gray-400 text-sm leading-none flex-shrink-0',
    notStarted: 'h-4 w-4 text-gray-300 flex-shrink-0',
  },
  mockExamsTable: {
    table: 'w-full',
    head: 'sticky top-0 bg-white z-10',
    headRow: 'border-b border-gray-200',
    headCell: 'text-left py-3 px-4 font-semibold text-gray-700',
    headCellScore: 'text-left py-3 px-4 font-semibold text-gray-700 w-80',
    bodyRow: 'border-b border-gray-100 hover:bg-gray-50 transition-all duration-200',
    bodyCell: 'py-3 px-4',
    examRow: 'flex items-center gap-2.5',
    examTitle: 'font-medium text-gray-900',
    scoreRow: 'flex items-center gap-3 w-full',
    scoreTrack: 'flex-1 bg-gray-200 rounded-full h-3',
    scoreFill: 'h-3 rounded-full transition-all duration-300',
    scoreTextBase:
      'min-w-[5rem] text-right tabular-nums inline-flex items-baseline justify-end gap-0',
    scoreTextIncomplete: 'text-lg font-bold text-gray-400',
    scoreValue: 'text-md font-semibold text-gray-900',
    scoreTotal: 'text-sm font-normal text-gray-400',
  },
  mockExamsCard: {
    container: 'bg-white rounded-2xl p-8 shadow-sm flex flex-col h-full',
    header: 'flex items-start justify-between gap-6 mb-6',
    title: 'text-xl font-semibold',
    subtitle: 'text-sm text-gray-600 mt-1',
    scoreBox: 'rounded-2xl bg-gray-50 p-4 text-right flex-shrink-0',
    scoreValue: 'text-3xl font-bold text-gray-900 tabular-nums leading-none',
    scoreLabel: 'text-xs text-gray-500 mt-1',
    tableWrapper: 'overflow-x-hidden flex-1',
  },
  whatsNext: {
    section: 'h-full',
    title: 'text-sm font-semibold text-gray-900 mb-3',
    grid: 'grid grid-cols-1 sm:grid-cols-3 gap-4',
    card: 'bg-white shadow-sm rounded-xl overflow-hidden flex flex-col w-full min-w-0',
    imageWrapper: 'relative w-full h-24 bg-gray-50',
    image: 'object-cover',
    body: 'p-3 flex flex-col flex-1',
    itemTitle: 'text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-2',
    tags: 'flex flex-wrap gap-1 mb-2',
    streamTypeTag:
      'inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-cyan-100 text-cyan-800',
    statusTagBase: 'px-2 py-0.5 rounded-full text-[10px] font-medium',
    progressHeader: 'flex items-center justify-between text-[11px] text-gray-500 mb-1.5',
    progressValue: 'font-medium text-gray-800 tabular-nums',
    progressBar: 'h-1 mb-3',
    actionButton: 'w-full h-7 text-xs mt-auto',
    status: {
      not_started: 'bg-gray-100 text-gray-600',
      in_progress: 'bg-amber-50 text-amber-700',
      completed: 'bg-aralin-green/10 text-aralin-green',
    },
  },
  leanStreamCard: {
    container: 'rounded-2xl bg-white shadow-sm p-6 flex flex-col h-full',
    title: 'text-lg font-semibold text-gray-900 mb-4',
    countRow: 'flex items-baseline gap-1',
    count: 'text-4xl font-bold text-gray-900 tabular-nums leading-none',
    countLabel: 'text-sm text-gray-500',
    progress: 'h-1 mt-4',
  },
} as const;

export function cetReadinessStreamSegmentClass(filled: boolean): string {
  return cn(
    cetReadiness.streamProgressBar.segment,
    filled
      ? cetReadiness.streamProgressBar.segmentFilled
      : cetReadiness.streamProgressBar.segmentEmpty
  );
}

export function cetReadinessBadgeModalStatusClass(earned: boolean): string {
  return cn(
    cetReadiness.badgeModal.statusBase,
    earned ? cetReadiness.badgeModal.statusEarned : cetReadiness.badgeModal.statusLocked
  );
}

export function cetReadinessBadgeImageClass(earned: boolean): string {
  return cn(
    cetReadiness.badgeModal.imageBase,
    !earned && cetReadiness.badgeModal.imageLocked
  );
}

export function cetReadinessBadgeShieldClass(earned: boolean): string {
  return cn(
    ...cetReadiness.badgeShield.base,
    earned ? cetReadiness.badgeShield.earned : cetReadiness.badgeShield.locked
  );
}

export function cetReadinessBadgeShieldImageClass(earned: boolean): string {
  return cn(
    cetReadiness.badgeShield.imageBase,
    earned ? cetReadiness.badgeShield.imageEarned : cetReadiness.badgeShield.imageLocked
  );
}

export function cetReadinessMockExamScoreClass(completed: boolean): string {
  return cn(
    cetReadiness.mockExamsTable.scoreTextBase,
    !completed && cetReadiness.mockExamsTable.scoreTextIncomplete
  );
}

export function cetReadinessWhatsNextStatusClass(status: CetItemStatus): string {
  return cn(
    cetReadiness.whatsNext.statusTagBase,
    cetReadiness.whatsNext.status[status]
  );
}

const ADMIN_DASHBOARD_SUBJECT_COLORS: Record<string, string> = {
  Mathematics: 'var(--aralin-red)',
  English: 'var(--aralin-blue)',
  Science: 'var(--aralin-green)',
  Filipino: 'var(--aralin-yellow)',
  'Araling Panlipunan': 'var(--aralin-orange)',
  Arts: 'var(--aralin-purple)',
  'Physical Education': 'var(--aralin-pink)',
};

const ADMIN_DASHBOARD_FALLBACK_COLORS = [
  '#8B5CF6',
  '#06B6D4',
  '#84CC16',
  '#F43F5E',
  '#6366F1',
  '#EC4899',
  '#14B8A6',
  '#F97316',
  '#A855F7',
  '#0EA5E9',
];

function getAdminDashboardSubjectColor(subjectName: string | null | undefined): string {
  if (!subjectName) {
    return '#6B7280';
  }

  if (ADMIN_DASHBOARD_SUBJECT_COLORS[subjectName]) {
    return ADMIN_DASHBOARD_SUBJECT_COLORS[subjectName];
  }

  const lowerSubjectName = subjectName.toLowerCase();
  for (const [key, color] of Object.entries(ADMIN_DASHBOARD_SUBJECT_COLORS)) {
    if (lowerSubjectName.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerSubjectName)) {
      return color;
    }
  }

  let hash = 0;
  for (let i = 0; i < subjectName.length; i++) {
    hash = ((hash << 5) - hash) + subjectName.charCodeAt(i);
    hash = hash & hash;
  }

  return ADMIN_DASHBOARD_FALLBACK_COLORS[Math.abs(hash) % ADMIN_DASHBOARD_FALLBACK_COLORS.length];
}

function getAdminDashboardScoreColor(score: number): string {
  if (score > 80) {
    return 'var(--aralin-green)';
  }
  if (score >= 75) {
    return 'var(--aralin-yellow)';
  }
  return 'var(--aralin-red)';
}

export const adminDashboardChartAspectRatio: CSSProperties = {
  aspectRatio: '2/1',
};

export const adminDashboardGreenText: CSSProperties = {
  color: 'var(--aralin-green)',
};

export function adminDashboardTooltipStyle(tooltip: {
  content: string;
  x: number;
  y: number;
}): CSSProperties {
  const labelLength = tooltip.content.split(': ')[0]?.length ?? 0;
  return {
    backgroundColor: 'var(--aralin-color)',
    left: `${tooltip.x}%`,
    top: `${tooltip.y - 2}%`,
    transform: 'translateX(-50%)',
    width: `${Math.min(Math.max(labelLength * 8, 150), 300)}px`,
    textAlign: 'center',
  };
}

export function adminDashboardExamLabelStyle(params: {
  x: number;
  svgWidth: number;
  barWidth: number;
  examCount: number;
}): CSSProperties {
  return {
    left: `${(params.x / params.svgWidth) * 100}%`,
    top: '8px',
    width: params.examCount > 1 ? `${(params.barWidth / params.svgWidth) * 100}%` : 'auto',
  };
}

export function adminDashboardSubjectBar(
  score: number,
  subjectName: string | null | undefined
): CSSProperties {
  return {
    width: `${roundScore(score)}%`,
    backgroundColor: getAdminDashboardSubjectColor(subjectName),
  };
}

export function adminDashboardScoreBar(score: number): CSSProperties {
  const rounded = roundScore(score);
  return {
    width: `${rounded}%`,
    backgroundColor: getAdminDashboardScoreColor(rounded),
  };
}

export function adminDashboardSectionsScoreBar(score: number): CSSProperties {
  const rounded = roundScore(score);
  return {
    width: `${rounded}%`,
    backgroundColor: getSectionsTabScoreBarColor(rounded),
  };
}

export const adminDashboard = {
  shell: 'flex',
  main: 'flex-1 p-6 bg-aralin-bg',
  content: 'px-6 pb-6',
  intro: 'mb-8',
  tabsWrap: 'mb-6',
  tabs: 'flex border-b border-gray-200',
  filterRow: 'flex justify-start gap-4',
  filterCard: 'bg-white rounded-lg p-2',
  rowGap3: 'flex items-center gap-3',
  filterIconWrap: 'w-8 h-8 bg-aralin-green/10 rounded-md flex items-center justify-center',
  filterIcon: 'w-4 h-4 text-aralin-green',
  rowGap2: 'flex items-center gap-2',
  filterLabel: 'text-sm font-medium text-gray-700',
  filterTrigger: 'min-w-[120px] h-8 text-sm',
  section: 'mb-8 pt-6',
  statsGrid: 'grid grid-cols-1 md:grid-cols-4 gap-6',
  statCard: 'bg-gray-50 p-8 rounded-xl bg-white',
  statImage: 'w-12 h-12',
  statLabel: 'text-base text-gray-600',
  statValue: 'text-3xl font-bold text-aralin-green',
  card: 'bg-white rounded-2xl p-8 shadow-sm',
  emptyPadLg: 'flex items-center justify-center py-12',
  center: 'text-center',
  emptyIconWrapMd: 'w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4',
  emptyIconLg: 'w-8 h-8 text-gray-400',
  emptyTitle: 'text-gray-500 mb-2',
  emptyHint: 'text-sm text-gray-400',
  chartsRow: 'mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6',
  cardHeader: 'flex items-center justify-between mb-4',
  cardTitle: 'text-xl font-semibold',
  cardSubtitle: 'text-sm text-gray-600',
  chartInner: 'w-full mx-auto',
  chartCenter: 'flex items-center justify-center',
  spinnerLg: 'animate-spin rounded-full h-12 w-12 border-b-2 border-aralin-green mx-auto mb-4',
  muted: 'text-gray-500',
  emptyIconWrapLg: 'w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4',
  relative: 'relative',
  full: 'w-full h-full',
  chartBar: 'cursor-pointer hover:opacity-80 transition-opacity duration-200',
  tooltip: 'absolute z-10 px-3 py-2 text-white rounded-lg shadow-lg pointer-events-none',
  tooltipText: 'text-sm font-bold',
  chartLabels: 'absolute bottom-0 left-0 right-0 h-10',
  examLabel: 'absolute text-base text-gray-600 text-center transform -translate-x-1/2 px-2 leading-tight whitespace-nowrap truncate',
  tableScroll: 'max-h-[600px] overflow-y-auto',
  table: 'w-full',
  tableHead: 'sticky top-0 bg-white z-10',
  tableHeadRow: 'border-b border-gray-200',
  th: 'text-left py-3 px-4 font-semibold text-gray-700',
  thSort: 'text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50',
  thW20: 'text-left py-3 px-4 font-semibold text-gray-700 w-20',
  thW24: 'text-left py-3 px-4 font-semibold text-gray-700 w-24',
  thW32: 'text-left py-3 px-4 font-semibold text-gray-700 w-32',
  thW80: 'text-left py-3 px-4 font-semibold text-gray-700 w-80',
  tr: 'border-b border-gray-100 hover:bg-gray-50',
  td: 'py-3 px-4',
  subjectToggle: 'p-3 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors',
  subjectHeader: 'flex items-center justify-between mb-2',
  subjectScore: 'text-sm font-bold text-aralin-green',
  barTrackSm: 'w-full bg-gray-200 rounded-full h-2',
  barFillSm: 'h-2 rounded-full transition-all duration-300',
  subList: 'bg-gray-50',
  subRow: 'px-4 py-3',
  indent: 'ml-6',
  subName: 'text-sm font-medium text-gray-800',
  stack: 'space-y-3',
  groupTitle: 'font-semibold text-gray-800 mb-3',
  tinyMuted: 'text-xs text-gray-400',
  listCard: 'bg-white rounded-lg p-4 border border-gray-200',
  overflowX: 'overflow-x-auto',
  cardTitleSpaced: 'text-xl font-semibold mb-2',
  goodIconWrap: 'w-6 h-6 bg-green-100 rounded-full flex items-center justify-center',
  goodIcon: 'w-4 h-4 text-green-600',
  goodText: 'text-sm font-semibold text-green-600',
  colDivider: 'lg:border-l lg:border-gray-200 lg:pl-6',
  badIconWrap: 'w-6 h-6 bg-red-100 rounded-full flex items-center justify-center',
  badIcon: 'w-4 h-4 text-red-600',
  badText: 'text-sm font-semibold text-red-600',
  emptyStatePad: 'py-12 text-center',
  trClick: 'border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-all duration-200',
  rankFallback: 'w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center text-white font-bold text-xs',
  scoreRow: 'flex items-center gap-3 w-full',
  barTrackFlex: 'flex-1 bg-gray-200 rounded-full h-3',
  barFill: 'h-3 rounded-full transition-all duration-300',
  scoreLgMin: 'text-lg font-bold min-w-[4rem] text-right',
  tdStrong: 'py-3 px-4 font-medium text-gray-900',
  tdMuted: 'py-3 px-4 text-gray-600',
  twoCol: 'grid grid-cols-1 lg:grid-cols-2 gap-6',
  studentRow: 'flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200',
  col: 'flex flex-col',
  semibold: 'font-semibold',
  tiny: 'text-xs text-gray-500',
  twoColEqual: 'grid grid-cols-2 gap-6',
  listHeader: 'flex items-center gap-2 mb-4',
  listMeta: 'flex flex-col items-start gap-1 w-64',
  listItem: 'flex flex-col gap-1 p-3 bg-white rounded-lg hover:bg-gray-50 transition-all duration-200 border border-gray-200 mb-2',
  rowBetween: 'flex items-center justify-between',
  listItemStats: 'flex flex-wrap gap-4 mt-1 text-xs',
  textGood: 'text-green-700',
  textBad: 'text-red-700',
  barTrack: 'w-full bg-gray-200 rounded-full h-3',
  scoreLgFull: 'text-lg font-bold text-aralin-green w-full text-right',
  bodyMuted: 'text-sm text-gray-600 mb-4',
  countChip: 'ml-3 text-xs text-gray-600 bg-gray-100 rounded-md px-2 py-1',
  overflowXHidden: 'overflow-x-hidden',
  name: 'font-medium text-gray-900',
  textDark: 'text-gray-900',
  barTrackFixed: 'w-64 bg-gray-200 rounded-full h-3',
  scoreLg: 'text-lg font-bold',
  nameAlt: 'text-gray-900 font-medium',
  rowBetweenAlt: 'flex justify-between items-center',
  gap4: 'flex gap-4',
  cardSpaced: 'mb-8 bg-white rounded-2xl p-8 shadow-sm',
  cardHeaderLg: 'flex items-center justify-between mb-6',
  emptyPad: 'flex items-center justify-center py-8',
  emptyIconWrap: 'w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3',
  emptyIcon: 'w-6 h-6 text-gray-400',
  emptySm: 'text-gray-500 text-sm',
  spinnerMd: 'animate-spin rounded-full h-8 w-8 border-b-2 border-aralin-green mx-auto mb-3',
  errorIconWrap: 'w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3',
  errorIcon: 'w-6 h-6 text-red-500',
  errorTitle: 'text-red-600 text-sm font-medium mb-2',
  errorText: 'text-gray-500 text-sm mb-4',
  spinnerWhite: 'animate-spin rounded-full h-4 w-4 border-b-2 border-white',
  iconSm: 'w-4 h-4',
  hint: 'text-xs text-gray-400 mt-2',
  markdown: 'text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none',
  mdDivider: 'border-t border-gray-200 my-6',
  mdH3: 'text-lg font-semibold text-gray-700 mt-4 mb-2',
  mdH4: 'text-base font-semibold text-cyan-700 mt-3 mb-1 list-none',
  mdP: 'mb-2',
  mdUl: 'list-disc list-inside mb-2 space-y-1',
  mdLi: 'text-gray-700',
  tableScrollSm: 'max-h-96 overflow-y-auto',
  tab: {
    base: 'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
    active: 'border-aralin-green text-aralin-green',
    inactive: 'border-transparent text-gray-500 hover:text-gray-700',
  },
  chevron: 'w-4 h-4 text-gray-500 transition-transform',
  chevronOpen: 'rotate-180',
  rankBadge: 'w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm',
  rank1: 'bg-yellow-500',
  rank2: 'bg-gray-400',
  rank3: 'bg-amber-600',
  rankOther: 'bg-gray-300',
  sectionRowSelected: 'bg-aralin-green/10 border-l-4 border-l-aralin-green shadow-sm',
  nameBase: 'font-medium',
  nameActive: 'text-aralin-green',
  nameInactive: 'text-gray-900',
  actionButton: 'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
  actionButtonDisabled: 'bg-gray-200 text-gray-400 cursor-not-allowed',
  actionButtonReady: 'bg-aralin-green text-white hover:bg-aralin-green/90',
  actionButtonBusy: 'bg-gray-400 text-gray-400 cursor-not-allowed',
} as const;
export function adminDashboardTabClass(isActive: boolean): string {
  return cn(
    adminDashboard.tab.base,
    isActive ? adminDashboard.tab.active : adminDashboard.tab.inactive
  );
}

export function adminDashboardChevronClass(isExpanded: boolean): string {
  return cn(adminDashboard.chevron, isExpanded && adminDashboard.chevronOpen);
}

export function adminDashboardRankClass(rank: number): string {
  return cn(
    adminDashboard.rankBadge,
    rank === 1 && adminDashboard.rank1,
    rank === 2 && adminDashboard.rank2,
    rank === 3 && adminDashboard.rank3,
    rank !== 1 && rank !== 2 && rank !== 3 && adminDashboard.rankOther
  );
}

export function adminDashboardSectionRowClass(isSelected: boolean): string {
  return cn(adminDashboard.trClick, isSelected && adminDashboard.sectionRowSelected);
}

export function adminDashboardSectionNameClass(isSelected: boolean): string {
  return cn(adminDashboard.nameBase, isSelected ? adminDashboard.nameActive : adminDashboard.nameInactive);
}

export function adminDashboardGenerateButtonClass(isDisabled: boolean): string {
  return cn(
    adminDashboard.actionButton,
    isDisabled ? adminDashboard.actionButtonDisabled : adminDashboard.actionButtonReady
  );
}

export function adminDashboardRetryButtonClass(isGenerating: boolean): string {
  return cn(
    adminDashboard.actionButton,
    isGenerating ? adminDashboard.actionButtonBusy : adminDashboard.actionButtonReady
  );
}

export const adminExams = {
  shell: 'flex items-start h-screen overflow-hidden',
  loading: 'flex-1 flex items-center justify-center',
  main: 'flex-1 self-stretch overflow-y-auto bg-aralin-bg transition-all duration-300 px-6 py-6',
  content: 'w-full max-w-full px-6',
  headerRow: 'flex items-start justify-between gap-4 flex-wrap',
  title: 'text-4xl font-medium mb-2 text-gray-900',
  subtitle: 'mb-8 text-gray-600',
  toolbar: 'flex items-center justify-between mb-4',
  countRow: 'flex gap-4 items-center',
  count: 'text-sm text-gray-600',
  gradeFilter: 'bg-white rounded-lg p-2',
  gradeFilterInner: 'flex items-center gap-3',
  gradeFilterIconWrap: 'w-8 h-8 bg-aralin-green/10 rounded-md flex items-center justify-center',
  gradeFilterIcon: 'w-4 h-4 text-aralin-green',
  gradeFilterControls: 'flex items-center gap-2',
  gradeFilterLabel: 'text-sm font-medium text-gray-700',
  gradeSelectTrigger: 'min-w-[120px] h-8 text-sm',
  sortIcon: 'w-4 h-4 text-aralin-green',
  tableScroll: 'overflow-x-auto',
  tableCard:
    'rounded-md overflow-hidden border border-gray-200 shadow-sm min-w-[800px] mb-10 bg-white',
  table: 'w-full text-sm bg-white table-fixed',
  thead: 'bg-gray-50 border-b border-gray-200',
  headRow: 'text-left',
  thSortInner: 'inline-flex items-center gap-2',
  thTitle: 'p-3 w-[28%] text-gray-700 select-none cursor-pointer',
  thGrade: 'p-3 w-[12%] text-gray-700',
  thItems: 'p-3 w-[10%] text-gray-700 select-none cursor-pointer',
  thTime: 'p-3 w-[10%] text-gray-700 select-none cursor-pointer',
  thDescription: 'p-3 w-[30%] text-gray-700',
  thActions: 'p-3 w-[10%]',
  emptyCell: 'p-10 text-center text-gray-500',
  bodyRow:
    'border-t border-gray-200 hover:bg-blue-50/50 transition-colors duration-150 group',
  tdTitle: 'p-3 w-[28%]',
  titleLink:
    'text-gray-700 group-hover:text-cyan-900 group-hover:font-bold group-hover:underline transition-all duration-200 ease-in-out',
  tdGrade: 'p-3 w-[12%] text-gray-700',
  tdItems: 'p-3 w-[10%] text-gray-700',
  tdTime: 'p-3 w-[10%] text-gray-700',
  tdDescription: 'p-3 w-[30%] text-gray-700',
  descriptionClamp: 'line-clamp-2',
  tdActions: 'p-3 w-[10%] text-right',
  viewButton: 'bg-cyan-900 hover:bg-cyan-900/90',
  viewIcon: 'w-4 h-4',
} as const;

export const adminExamDetailMainStyle: CSSProperties = {
  scrollbarGutter: 'stable',
};

export const adminExamDetail = {
  shell: 'fixed inset-0 flex items-start overflow-hidden',
  main: 'flex-1 h-full p-6 overflow-y-scroll overflow-x-hidden overscroll-contain scroll-pt-24',
  backWrap: 'mb-4',
  backLink: 'text-cyan-900 hover:underline',
  content: 'max-w-7xl mx-auto',
  loading: 'flex items-center justify-center py-24',
  notFound: 'text-center py-24 text-gray-600',
  header: 'pb-3 md:pb-4 mb-3 border-b border-gray-200',
  headerRow: 'flex items-start justify-between gap-3',
  title: 'text-base md:text-lg font-semibold text-cyan-900 truncate',
  reportButton:
    'flex items-center gap-2 bg-[#ec6f6f] text-white hover:bg-[#d85a5a] shadow-md border-0 font-semibold',
  reportIcon: 'w-4 h-4',
  meta: 'mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-700',
  chip: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 border',
  chipValue: 'font-medium',
  chipLabel: 'text-gray-500',
  chipSubjectValue: 'font-medium text-gray-800',
  description: 'mt-2 text-xs leading-snug text-gray-600 line-clamp-3',
  layout: 'flex gap-6',
  subjectNav: 'w-48 shrink-0 flex flex-col gap-1 pr-2',
  subjectButton: {
    base: 'text-left px-4 py-2 rounded-sm transition-colors font-medium text-sm',
    active: 'bg-cyan-900 text-white font-semibold shadow-inner',
    inactive: 'text-gray-700 hover:bg-gray-200',
  },
  questionsCol: 'flex-1 min-w-0 max-w-3xl',
  navCol: 'w-48 shrink-0',
  navLabel: 'text-xs font-medium text-gray-500 mb-1 px-1',
  navGrid: 'grid grid-cols-6 gap-1',
  navButton: 'w-7 h-7 rounded-full border text-[11px] text-gray-700 bg-white hover:bg-gray-50',
  scrollTop:
    'fixed bottom-6 right-6 w-10 h-10 rounded-full bg-cyan-900 text-white shadow-lg hover:bg-cyan-900/90 focus:outline-none flex items-center justify-center',
  scrollTopIcon: 'w-5 h-5',
} as const;

export function adminExamDetailSubjectButtonClass(isActive: boolean): string {
  return cn(
    adminExamDetail.subjectButton.base,
    isActive
      ? adminExamDetail.subjectButton.active
      : adminExamDetail.subjectButton.inactive
  );
}

export const adminGrading = {
  shell: 'flex',
  main: 'flex-1 self-stretch overflow-y-auto bg-aralin-bg transition-all duration-300 px-6 py-6',
  content: 'w-full max-w-full px-6',
  loading: 'flex items-center justify-center min-h-[60vh]',
  headerRow: 'flex items-start justify-between gap-4 flex-wrap',
  title: 'text-4xl font-medium mb-2 text-gray-900',
  subtitle: 'mb-8 text-gray-600',
  toolbar: 'flex items-center justify-between mb-4 flex-wrap gap-4',
  countRow: 'flex gap-4 items-center',
  count: 'text-sm text-gray-600',
  toolbarRight: 'flex items-center gap-3 flex-wrap',
  searchWrap: 'relative',
  searchIcon: 'absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400',
  searchInput:
    'pl-10 pr-4 h-8 w-64 text-sm border-gray-300 focus:border-cyan-900 focus:ring-cyan-900',
  filterCard: 'bg-white rounded-lg p-2',
  row: 'flex items-center gap-2',
  rowGap3: 'flex items-center gap-3',
  filterInner: 'flex items-center gap-3',
  filterIconWrap: 'w-8 h-8 bg-aralin-green/10 rounded-md flex items-center justify-center',
  filterIcon: 'w-4 h-4 text-aralin-green',
  filterControl: 'flex items-center gap-2',
  filterLabel: 'text-sm font-medium text-gray-700',
  batchSelect: 'min-w-[120px] h-8 text-sm',
  examSelect: 'min-w-[200px] h-8 text-sm',
  examBanner:
    'mb-6 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200 shadow-md p-5',
  examBannerInner: 'flex items-start gap-6 flex-wrap',
  examBannerItem: 'flex items-center gap-3 min-w-[200px]',
  examBannerItemSm: 'flex items-center gap-3 min-w-[150px]',
  examNameIconWrap: 'w-12 h-12 bg-cyan-900 rounded-lg flex items-center justify-center shrink-0',
  examNameIcon: 'w-6 h-6 text-white',
  examBannerLabel: 'text-xs font-medium text-gray-600 uppercase tracking-wide mb-1',
  examBannerValue: 'text-base font-semibold text-gray-900',
  examBannerValueLg: 'text-lg font-semibold text-gray-900',
  examBannerDivider: 'h-12 w-px bg-gray-300',
  examStatIconWrap: 'w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center shrink-0',
  examStatIcon: 'w-5 h-5 text-cyan-900',
  examBannerDesc: 'flex-1 min-w-[250px]',
  examBannerDescText: 'text-sm text-gray-700 line-clamp-2',
  tableScroll: 'overflow-x-auto',
  tableCard:
    'rounded-md overflow-hidden border border-gray-200 shadow-sm min-w-[900px] mb-10 bg-white',
  table: 'w-full text-sm bg-white table-fixed',
  thead: 'bg-gray-50 border-b border-gray-200',
  headRow: 'text-left',
  thSortable: 'p-3 w-[14%] text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors',
  thSortableSection:
    'p-3 w-[12%] text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors',
  thSortInner: 'flex items-center gap-2',
  icon: 'w-4 h-4',
  sortIconMuted: 'w-4 h-4 text-gray-400',
  thAction: 'p-3 text-right',
  emptyCell: 'p-10 text-center text-gray-500',
  bodyRow: 'border-t border-gray-200 hover:bg-blue-50/50 transition-colors duration-150',
  td: 'p-3 w-[14%] text-gray-700',
  tdSection: 'p-3 w-[12%] text-gray-700',
  tdScore: 'p-3 w-[14%]',
  scoreRow: 'flex items-center gap-3',
  scoreTrack: 'w-32 bg-gray-200 rounded-full h-3 shrink-0',
  scoreFill: 'h-3 rounded-full transition-all duration-300',
  scoreValue: 'text-lg font-bold w-12 text-right',
  scoreNa: 'text-gray-500 w-12 text-right',
  tdAction: 'p-3 text-right',
  gradeButton: 'flex items-center gap-2 ml-auto',
  scrollTop:
    'fixed bottom-6 right-6 w-10 h-10 rounded-full bg-cyan-900 text-white shadow-lg hover:bg-cyan-900/90 focus:outline-none flex items-center justify-center z-50',
  scrollTopIcon: 'w-5 h-5',
  detail: 'max-w-7xl mx-auto',
  backWrap: 'mb-4',
  detailHeader: 'pb-3 md:pb-4 mb-3 border-b border-gray-200',
  detailHeaderRow: 'flex items-start justify-between gap-3',
  detailHeaderInfo: 'flex-1',
  detailTitle: 'text-base md:text-lg font-semibold text-cyan-900 truncate',
  detailStudent: 'text-sm text-gray-600 mt-1',
  detailActions: 'flex items-center gap-2',
  unsaved: 'text-sm text-amber-600 font-medium',
  saveButton: 'flex items-center gap-2',
  meta: 'mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-700',
  chip: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 border',
  chipValue: 'font-medium',
  chipLabel: 'text-gray-500',
  questionsLayout: 'flex gap-6',
  subjectNav: 'w-48 shrink-0 flex flex-col gap-1 pr-2',
  subjectButton: {
    base: 'text-left px-4 py-2 rounded-sm transition-colors font-medium text-sm',
    active: 'bg-cyan-900 text-white font-semibold shadow-inner',
    inactive: 'text-gray-700 hover:bg-gray-200',
  },
  questionsCol: 'flex-1 min-w-0 max-w-3xl',
  questionsList: 'space-y-10',
  questionCard: {
    base: 'bg-white border rounded-md shadow-sm p-6 space-y-4 scroll-mt-24',
    correct: 'border-l-4 border-l-green-600/80',
    incorrect: 'border-l-4 border-l-red-600/80',
  },
  questionInner: 'px-6',
  questionHeader: 'flex justify-between items-center text-sm text-gray-500 font-medium mb-4',
  questionHeaderLeft: 'flex items-center gap-2',
  checkIcon: 'text-green-600',
  xIcon: 'text-red-600',
  pointsBadge: 'text-xs bg-gray-100 px-2 py-0.5 rounded',
  questionTextWrap: 'text-center space-y-1 my-8 px-4',
  questionText:
    'text-base font-medium text-gray-800 mb-4 break-words overflow-hidden whitespace-normal leading-relaxed max-w-full text-center',
  questionImageWrap: 'flex justify-center mb-4',
  questionImage: 'object-contain w-full max-w-[800px] h-auto rounded-lg border border-gray-200',
  choicesGrid: 'grid grid-cols-2 gap-4 mb-6',
  choice: {
    base: 'relative pl-8 pr-4 pt-6 pb-3 rounded-md text-center min-h-[56px] flex flex-col items-center justify-center break-words',
    default: 'bg-gray-100 text-gray-800',
    correctSelected: 'bg-green-100 text-green-800 border border-green-500',
    wrongSelected: 'bg-red-100 text-red-800 border border-red-500',
    correct: 'bg-green-100 border border-green-500 text-green-800',
  },
  choiceLetter:
    'absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/80 text-gray-700 border',
  choiceImageWrap: 'w-full',
  choiceImageInner: 'flex justify-center mb-2',
  choiceImage: 'object-contain w-full max-w-[150px] h-auto rounded-md',
  choiceText: 'text-sm break-words overflow-visible whitespace-normal leading-relaxed',
  solution: 'mb-6 p-4 border rounded-lg bg-gray-50',
  solutionLabel: 'text-sm font-semibold text-gray-700 mb-2 block',
  solutionImage: 'max-w-full h-auto rounded-md',
  solutionLink: 'text-blue-600 hover:underline',
  gradingInput: 'border-t pt-4 mt-4',
  gradingField: 'mb-4',
  gradingLabel: 'mb-2 block',
  scoreInput: 'w-full',
  feedbackInput: 'w-full min-h-[100px] p-3 border rounded-md resize-y',
  gradedStatus: 'mt-2 text-sm text-green-600 flex items-center gap-1',
  navCol: 'w-48 shrink-0',
  navLabel: 'text-xs font-medium text-gray-500 mb-1 px-1',
  navGrid: 'grid grid-cols-6 gap-1',
  navButton: {
    base: 'w-7 h-7 rounded-full border text-[11px] text-gray-700 bg-white hover:bg-gray-50',
    graded: 'border-green-500 bg-green-50',
  },
  errorCard: 'bg-white rounded-lg shadow p-8 text-center',
  errorText: 'text-gray-600',
} as const;

export function adminGradingSubjectButtonClass(isActive: boolean): string {
  return cn(
    adminGrading.subjectButton.base,
    isActive ? adminGrading.subjectButton.active : adminGrading.subjectButton.inactive
  );
}

export function adminGradingQuestionCardClass(gotItRight: boolean): string {
  return cn(
    adminGrading.questionCard.base,
    gotItRight ? adminGrading.questionCard.correct : adminGrading.questionCard.incorrect
  );
}

export function adminGradingChoiceClass(isCorrect: boolean, isSelected: boolean): string {
  const isCorrectSelected = isCorrect && isSelected;
  const isWrongSelected = !isCorrect && isSelected;
  return cn(
    adminGrading.choice.base,
    isCorrectSelected && adminGrading.choice.correctSelected,
    isWrongSelected && adminGrading.choice.wrongSelected,
    isCorrect && !isSelected && adminGrading.choice.correct,
    !isCorrect && !isSelected && adminGrading.choice.default
  );
}

export function adminGradingNavButtonClass(isGraded: boolean): string {
  return cn(adminGrading.navButton.base, isGraded && adminGrading.navButton.graded);
}

export const adminReviewers = {
  shell: 'flex',
  main: 'flex-1 p-6 bg-aralin-bg',
  content: 'px-6 pt-6 pb-8',
  header: 'mb-6',
  title: 'text-4xl font-medium mb-2 text-gray-900',
  subtitle: 'text-gray-600 mt-1',
  toolbar: 'mb-6 flex flex-col sm:flex-row gap-3',
  toolbarLeft: 'flex flex-1 gap-3',
  searchWrap: 'relative flex-1 max-w-md mt-1',
  searchIcon: 'absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400',
  searchInput:
    'w-full pl-10 pr-4 py-[6px] border bg-white border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-transparent placeholder:text-sm',
  row: 'flex items-center gap-2',
  filterSelect: 'w-[180px] bg-white',
  toolbarRight: 'flex items-center gap-2',
  viewToggle: 'flex gap-1 bg-gray-200 p-1 rounded-lg w-fit',
  viewToggleBtn:
    'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
  viewToggleBtnActive: 'bg-white text-gray-900 shadow-sm',
  viewToggleBtnInactive: 'text-gray-600 hover:text-gray-900',
  viewToggleLabel: 'hidden sm:inline',
  uploadButton: 'whitespace-nowrap',
  icon: 'w-4 h-4',
  empty: 'bg-white rounded-lg border border-gray-200 p-12 text-center',
  emptyInner: 'flex flex-col items-center gap-3',
  emptyIconWrap: 'p-3 bg-gray-100 rounded-full',
  emptyIcon: 'text-gray-400',
  emptyTitle: 'text-lg font-medium text-gray-900',
  emptyText: 'text-gray-600 max-w-sm',
  modalOverlay:
    'fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm',
  modal:
    'bg-white rounded-2xl shadow-xl p-0 w-full max-w-2xl mx-4 flex flex-col max-h-[calc(100vh-2rem)]',
  modalHeader: 'flex items-center justify-between px-6 py-4 border-b border-gray-200',
  modalTitle: 'text-lg font-semibold',
  modalSubtitle: 'text-xs text-gray-500 mt-1',
  modalClose: 'p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700',
  modalCloseIcon: 'w-5 h-5',
  modalBody:
    'px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto min-h-0 relative',
  modalLoading:
    'absolute inset-0 z-10 bg-white/70 backdrop-blur-sm flex items-center justify-center',
  modalLoadingInner: 'text-center',
  spinner: 'animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-900 mx-auto mb-3',
  modalLoadingText: 'text-gray-600 text-sm',
  colSpan2: 'md:col-span-2',
  fieldLabel: 'text-sm text-gray-700',
  required: 'text-red-500',
  optional: 'text-gray-500',
  fieldMt: 'mt-1',
  descriptionInput:
    'mt-1 min-h-[96px] w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-xs md:placeholder:text-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring',
  charCount: 'mt-1 text-xs',
  charCountMax: 'text-red-600',
  charCountOk: 'text-gray-400',
  selectTrigger: 'mt-1 w-full',
  hint: 'text-xs text-gray-500 mt-1',
  tagsTrigger: 'min-w-[220px] justify-start',
  tagsPlaceholder: 'text-gray-500',
  tagsPreview: 'flex items-center gap-1 overflow-hidden',
  tagChip:
    'text-[11px] px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 whitespace-nowrap',
  tagMore:
    'text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 whitespace-nowrap',
  tagsMenu: 'w-72 p-0 overflow-hidden',
  tagsMenuHeader: 'px-3 py-2 border-b text-sm flex items-center justify-between',
  tagsMenuTitle: 'font-medium',
  tagsMenuCount: 'text-gray-500',
  tagsSearch: 'p-2 sticky top-0 bg-white border-b z-10',
  tagsSearchInput: 'h-8',
  tagsSearchActions: 'mt-2 flex items-center gap-2',
  tagsList: 'max-h-64 overflow-y-auto py-1',
  tagsEmpty: 'px-3 py-6 text-center text-sm text-gray-500',
  tagsFooter: 'px-3 py-2 flex items-center justify-end gap-2 bg-white',
  selectedTags: 'flex flex-wrap gap-2 mt-2',
  selectedTag: 'text-xs px-2 py-1 rounded-full bg-cyan-100 text-cyan-800',
  selectedTagRemove: 'mr-1 text-cyan-800/80',
  hiddenInput: 'hidden',
  dropzone:
    'flex flex-col items-center justify-center gap-2 rounded-lg p-6 text-center cursor-pointer border-2 border-dashed transition-colors',
  dropzoneError: 'border-red-300 bg-red-50',
  dropzoneDragging: 'border-green-400 bg-green-50',
  dropzoneIdle: 'border-gray-200 hover:bg-gray-50',
  dropzoneIcon: 'w-5 h-5',
  dropzoneIconDragging: 'w-5 h-5 text-green-600',
  dropzoneIconError: 'text-red-500',
  dropzoneIconIdle: 'text-gray-500',
  dropzoneText: 'text-sm text-gray-700',
  dropzoneHint: 'text-xs text-gray-500',
  filePreview: 'mt-3 flex items-center justify-between gap-3 rounded-lg border bg-gray-50 px-3 py-2',
  fileMeta: 'flex items-center gap-2 min-w-0',
  fileIcon: 'w-4 h-4 text-gray-600 shrink-0',
  fileName: 'text-sm text-gray-800 truncate',
  fileSize: 'text-xs text-gray-500 shrink-0',
  fileLink: 'text-sm text-cyan-800 underline truncate',
  fileRemove: 'text-red-600 hover:text-red-700 hover:bg-red-50',
  fileError: 'text-xs text-red-600 mt-2',
  thumbnailPreviewRow: 'flex items-center gap-3 min-w-0',
  thumbnailImage: 'w-10 h-10 object-cover rounded-md border',
  modalFooter: 'flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200',
  deleteName: 'font-medium',
} as const;

export function adminReviewersViewToggleClass(active: boolean): string {
  return cn(
    adminReviewers.viewToggleBtn,
    active ? adminReviewers.viewToggleBtnActive : adminReviewers.viewToggleBtnInactive
  );
}

export function adminReviewersCharCountClass(atMax: boolean): string {
  return cn(
    adminReviewers.charCount,
    atMax ? adminReviewers.charCountMax : adminReviewers.charCountOk
  );
}

export function adminReviewersDropzoneClass(hasError: boolean, isDragging: boolean): string {
  return cn(
    adminReviewers.dropzone,
    hasError && adminReviewers.dropzoneError,
    !hasError && isDragging && adminReviewers.dropzoneDragging,
    !hasError && !isDragging && adminReviewers.dropzoneIdle
  );
}

export function adminReviewersDropzoneIconClass(hasError: boolean): string {
  return cn(
    adminReviewers.dropzoneIcon,
    hasError ? adminReviewers.dropzoneIconError : adminReviewers.dropzoneIconIdle
  );
}

export const adminReviewerDetail = {
  shell: 'flex',
  notFoundMain: 'flex-1 bg-aralin-bg h-screen flex items-center justify-center',
  notFoundInner: 'text-center',
  notFoundIconRow: 'flex justify-center mb-4',
  notFoundIconWrap: 'p-3 bg-red-100 rounded-full',
  notFoundIcon: 'text-red-600',
  notFoundTitle: 'text-2xl font-semibold text-gray-900 mb-2',
  notFoundText: 'text-gray-600 mb-6',
  notFoundButton:
    'inline-flex items-center gap-2 px-4 py-2 bg-cyan-900 text-white rounded-md hover:bg-cyan-800 transition-colors',
  main: 'flex-1 bg-aralin-bg h-screen flex flex-col min-h-0',
  header: 'shrink-0 bg-white border-b border-gray-200 px-4 py-2',
  headerRow: 'flex min-w-0 items-center gap-3',
  backButton: 'shrink-0 p-1.5 hover:bg-gray-100 rounded-full transition-colors',
  backIcon: 'text-gray-600',
  title: 'truncate text-lg font-semibold text-gray-900',
  viewer: 'flex-1 min-h-0 overflow-hidden bg-white',
  pdf: 'h-full',
  progressBar: 'mt-2 pl-9',
} as const;

export const reviewersPage = adminReviewers;
export const reviewersViewToggleClass = adminReviewersViewToggleClass;
export const reviewerDetailPage = adminReviewerDetail;

export const forgotPasswordPageBackground: CSSProperties = {
  backgroundImage: 'var(--aralin-gradient)',
};

export const loginPageBackground = forgotPasswordPageBackground;

export const registerPageBackground = forgotPasswordPageBackground;

export const forgotPasswordPage = {
  shell: 'flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 md:px-8',
  inner: 'w-full flex justify-center px-2 sm:px-4',
  card: 'bg-white p-6 sm:p-8 md:p-10 rounded-2xl shadow-md w-full max-w-md space-y-6',
  title: 'text-2xl font-semibold text-gray-800 mb-1 text-center sm:text-center',
  subtitle: 'text-sm text-gray-600 mb-6 text-center sm:text-center',
  form: 'space-y-4',
  securityNotice: 'bg-amber-50 text-amber-800 text-sm text-center px-4 py-2 rounded-md',
  field: 'space-y-2',
  fieldSpaced: 'space-y-2 mb-6',
  error: 'text-red-600 text-sm flex items-center space-x-1',
  errorIcon: 'h-4 w-4',
  submit:
    'w-full bg-aralin-green hover:bg-aralin-green-dark text-white py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed',
  footer: 'text-center text-sm',
  footerStacked: 'text-center text-sm space-y-2',
  loginLink:
    'text-aralin-green font-medium hover:underline flex items-center justify-center space-x-1',
  backButton:
    'text-aralin-green font-medium hover:underline flex items-center justify-center space-x-1 w-full',
  resend:
    'text-aralin-green font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed',
  otpRow: 'flex justify-center gap-2',
  otpInput:
    'w-12 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-md focus:border-aralin-green focus:outline-none focus:ring-2 focus:ring-aralin-green/20 transition-all',
  passwordWrap: 'relative',
  passwordToggle: 'absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500',
  helpWrap: 'text-center mt-4',
  helpLink: 'text-white font-medium text-sm hover:underline',
} as const;

export const loginPage = {
  shell: 'flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 md:px-8',
  inner: 'w-full flex justify-center px-2 sm:px-4',
  form: 'bg-white p-6 sm:p-8 md:p-10 rounded-2xl shadow-md w-full max-w-md space-y-6',
  logoWrap: 'flex items-center justify-center mb-2',
  logoInner: 'max-w-[40%]',
  logo: 'h-auto w-full object-contain',
  schoolName: 'text-center text-lg font-semibold text-gray-700 mb-4',
  title: 'text-2xl font-semibold text-gray-800 mb-1 text-center sm:text-center',
  subtitle: 'text-sm text-gray-600 mb-4 text-center sm:text-center',
  notice: 'bg-green-50 border border-green-200 rounded-md p-4',
  noticeText: 'text-sm text-green-800 text-center',
  field: 'space-y-2',
  fieldHeader: 'flex justify-between items-center',
  fieldError: 'text-red-600 text-sm',
  passwordWrap: 'relative',
  passwordToggle: 'absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500',
  forgotWrap: 'text-left mt-2',
  forgotLink: 'text-aralin-green font-medium hover:underline text-sm',
  submit: 'w-full bg-aralin-green hover:bg-aralin-green-dark text-white py-2 rounded-md',
  submitSpinner: 'h-4 w-4 animate-spin',
  error: 'bg-red-50 border border-red-200 rounded-md p-4 mt-2',
  errorRow: 'flex items-start space-x-2',
  errorIconWrap: 'flex-shrink-0 mt-0.5',
  errorIcon: 'h-5 w-5 text-red-400',
  errorText: 'text-sm text-red-700',
  registerWrap: 'text-center text-sm text-gray-600',
  registerLink: 'font-medium text-aralin-green',
} as const;

export const privacyTerms = {
  shell: 'min-h-screen flex flex-col bg-white px-4',
  header: 'flex flex-col sm:flex-row justify-between items-center px-6 py-4 sm:pr-32 w-full gap-4',
  logo: 'cursor-pointer',
  loginButton: 'bg-aralin-green hover:bg-aralin-green-dark text-white px-6 py-2 rounded-4xl',
  main: 'flex-1 max-w-4xl mx-auto px-4 py-8',
  prose: 'prose prose-lg max-w-none',
  pageTitle: 'text-3xl font-bold text-aralin-green mb-8 text-center',
  section: 'mb-12',
  sectionTitle: 'text-2xl font-semibold text-aralin-green mb-6',
  body: 'space-y-4 text-gray-700',
  heading: 'text-xl font-semibold text-aralin-green mt-6 mb-3',
  list: 'list-disc pl-6 space-y-2',
  nestedList: 'list-disc pl-6 space-y-1 mt-2',
  subheading: 'text-lg font-semibold text-aralin-green mt-6 mb-3 ml-4',
  indent: 'ml-4',
  footer: 'py-6 bg-white border-t text-center text-sm text-gray-500',
  footerCopy: 'mb-2',
  brand: 'font-semibold text-aralin-green',
  homeLink: 'hover:underline cursor-pointer',
} as const;

export const registerPage = {
  shell: 'flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 md:px-10 py-8',
  card: 'w-full max-w-md sm:max-w-lg md:max-w-xl bg-white p-6 sm:p-8 md:p-10 rounded-2xl shadow-md space-y-6',
  logoWrap: 'flex justify-center mb-1',
  logo: 'h-auto w-auto max-w-[70%]',
  title: 'text-2xl font-semibold text-gray-800 mb-1 text-center sm:text-left',
  subtitle: 'text-sm text-gray-600 mb-4 text-center sm:text-left',
  formError: 'bg-red-100 text-red-700 text-sm text-center px-4 py-2 rounded-md font-medium',
  form: 'space-y-4',
  grid: 'grid grid-cols-1 md:grid-cols-2 gap-4',
  field: 'space-y-2',
  input: 'w-full',
  fieldError: 'text-red-600 text-sm',
  passwordWrap: 'relative',
  passwordToggle: 'absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500',
  submit: 'w-full bg-aralin-green hover:bg-aralin-green-dark text-white py-2 rounded-md mt-6',
  loginWrap: 'text-center text-sm text-gray-600',
  loginLink: 'font-medium text-aralin-green',
} as const;

export const settingsPage = {
  shell: 'flex h-screen overflow-hidden',
  main: 'flex-1 overflow-y-auto bg-aralin-bg transition-all duration-300 px-6 py-6',
  content: 'w-full max-w-full px-6',
  headerRow: 'flex items-start justify-between gap-4 flex-wrap',
  title: 'text-4xl font-medium mb-2 text-gray-900',
  subtitle: 'mb-8',
  tabs: 'mb-6 flex border-b border-gray-200',
  tab: {
    base: 'flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors duration-200 rounded-t-md',
    active: 'border-gray-700 text-gray-700',
    inactive: 'border-transparent text-gray-400 hover:text-gray-700',
  },
  tabIcon: 'w-4 h-4',
} as const;

export function settingsTabClass(isActive: boolean): string {
  return cn(
    settingsPage.tab.base,
    isActive ? settingsPage.tab.active : settingsPage.tab.inactive
  );
}

export const studentDashboardPage = {
  shell: 'flex',
  main: 'flex-1 p-6 bg-aralin-bg',
  content: 'px-6 pb-6',
  intro: 'mb-8 text-gray-600',
  categories: 'space-y-12',
  categoryHeader: 'mb-6',
  categoryTitle: 'text-2xl font-medium text-gray-900 mb-2',
  categoryDescription: 'text-gray-600',
  grid: 'grid grid-cols-[repeat(auto-fill,_minmax(400px,_0fr))] gap-10',
  cardWrap: 'flex justify-center',
} as const;

export const mockExamsPage = {
  shell: 'flex',
  main: 'flex-1 p-6 bg-aralin-bg',
  content: 'px-6 pb-6',
  title: 'text-4xl font-medium mb-2 text-gray-900',
  subtitle: 'mb-6',
  searchRow: 'flex justify-start',
  searchWrap: 'relative mb-8 max-w-md w-full',
  searchIcon: 'absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5',
  searchInput:
    'w-full pl-10 pr-4 py-2 border bg-white border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-transparent',
  grid: 'grid grid-cols-[repeat(auto-fill,_minmax(400px,_0fr))] gap-10',
  cardWrap: 'flex justify-center',
} as const;

export const dailyPracticePage = {
  shell: 'flex',
  main: 'flex-1 p-6 bg-aralin-bg',
  content: 'px-6 pb-6',
  title: 'text-4xl font-medium mb-2 text-gray-900',
  subtitle: 'mb-6',
  tabs: 'flex flex-wrap gap-3 mb-8',
  tab: {
    base: 'px-4 py-2 rounded-md border text-sm font-medium transition',
    active: 'bg-gray-800 text-white',
    inactive: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100',
  },
  grid: 'grid grid-cols-[repeat(auto-fill,_minmax(400px,_0fr))] gap-10',
  cardWrap: 'flex justify-center',
} as const;

export function dailyPracticeTabClass(isActive: boolean): string {
  return cn(
    dailyPracticePage.tab.base,
    isActive ? dailyPracticePage.tab.active : dailyPracticePage.tab.inactive
  );
}

export const dailyPracticeExamPageBackground = forgotPasswordPageBackground;

export const dailyPracticeExamPage = {
  errorShell: 'min-h-screen flex items-center justify-center bg-red-50',
  errorText: 'text-red-600 text-xl',
  shell: 'relative min-h-screen w-full',
  overlay: 'absolute inset-0 bg-[#F2F6F5]/25 pointer-events-none z-0',
  content: {
    base: 'relative z-10',
    offline: 'pt-12',
  },
  header: 'px-8 py-6 flex justify-between items-center',
  logo: 'h-16 w-auto',
  layout: 'flex justify-center w-full pt-6 md:pt-6 pb-20 md:pb-10',
  layoutInner: 'w-full max-w-7xl',
  columns: 'flex flex-col md:flex-row justify-center gap-4 md:gap-8 px-4',
  card: 'w-full md:w-[650px] lg:w-[1000px] min-h-[200px] bg-white rounded-3xl shadow-lg p-6 sm:p-6 md:p-10 flex flex-col space-y-4 md:space-y-6',
  questionArea: 'flex-1 flex flex-col space-y-4 md:space-y-6',
  tracker: 'flex-shrink-0 w-full md:w-52',
} as const;

export function dailyPracticeExamContentClass(isOffline: boolean): string {
  return cn(
    dailyPracticeExamPage.content.base,
    isOffline && dailyPracticeExamPage.content.offline
  );
}

export const mockExamPageBackground = dailyPracticeExamPageBackground;
export const mockExamPage = dailyPracticeExamPage;
export const mockExamContentClass = dailyPracticeExamContentClass;

export const sessionReportPage = {
  shell: 'flex no-print',
  main: {
    base: 'flex-1 bg-aralin-bg overflow-y-auto',
    analytics: 'h-screen',
  },
  header: 'sticky top-0 z-10 px-6 pt-6 bg-aralin-bg',
  headerRow: 'flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4',
  title: 'text-xl font-semibold',
  date: 'text-gray-600',
  actions: 'flex gap-2',
  refreshButton:
    'inline-flex items-center gap-2 px-4 py-2 rounded bg-gray-600 text-white hover:bg-gray-700 disabled:opacity-50',
  refreshSpinner: {
    base: 'w-4 h-4 border-b-2 border-white rounded-full',
    loading: 'animate-spin',
  },
  downloadButton:
    'inline-flex items-center gap-2 px-4 py-2 rounded bg-cyan-900 text-white hover:bg-cyan-800',
  tabs: 'flex border-b border-gray-200',
  tab: {
    base: 'flex items-center gap-2 px-4 py-2 font-medium',
    questions: 'ml-4',
    active: 'border-b-2 border-gray-700 text-gray-700',
    inactive: 'text-gray-400 hover:text-gray-700',
  },
  body: 'px-6 py-6',
  analyticsWrap: 'flex justify-center',
  analyticsInner: 'w-full max-w-4xl',
  insightsBanner: 'mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg',
  insightsRow: 'flex items-center gap-3',
  insightsSpinner: 'animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600',
  insightsText: 'text-blue-800 text-sm',
  questionsLayout: 'flex gap-6',
  subjectNav: 'w-48 shrink-0 flex flex-col gap-1 pr-2',
  subjectButton: {
    base: 'text-left px-4 py-2 rounded-sm transition-colors font-medium text-sm',
    active: 'bg-cyan-900 text-white font-semibold shadow-inner',
    inactive: 'text-gray-700 hover:bg-gray-200',
  },
  questionsCol: 'ml-30 flex-1 max-w-3xl',
  pdfOverlay: 'fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center',
  pdfModal: 'bg-white w-[90vw] h-[90vh] relative shadow-lg rounded',
  pdfClose: 'absolute top-2 right-2 z-10 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700',
} as const;

export function sessionReportMainClass(isQuestionsTab: boolean): string {
  return cn(
    sessionReportPage.main.base,
    !isQuestionsTab && sessionReportPage.main.analytics
  );
}

export function sessionReportRefreshSpinnerClass(isLoading: boolean): string {
  return cn(
    sessionReportPage.refreshSpinner.base,
    isLoading && sessionReportPage.refreshSpinner.loading
  );
}

export function sessionReportTabClass(isActive: boolean, isQuestionsTab = false): string {
  return cn(
    sessionReportPage.tab.base,
    isQuestionsTab && sessionReportPage.tab.questions,
    isActive ? sessionReportPage.tab.active : sessionReportPage.tab.inactive
  );
}

export function sessionReportSubjectButtonClass(isActive: boolean): string {
  return cn(
    sessionReportPage.subjectButton.base,
    isActive ? sessionReportPage.subjectButton.active : sessionReportPage.subjectButton.inactive
  );
}

export const reportsPage = {
  shell: 'flex h-screen overflow-hidden',
  main: 'flex-1 overflow-y-auto bg-aralin-bg transition-all duration-300 px-6 py-6',
  content: 'w-full max-w-full px-6',
  headerRow: 'flex items-start justify-between gap-4 flex-wrap',
  title: 'text-4xl font-medium mb-2 text-gray-900',
  subtitle: 'mb-4',
  notice: 'mb-6 bg-sky-100/60 rounded-lg p-3 flex items-center gap-3',
  noticeIcon: 'w-5 h-5 text-sky-600 flex-shrink-0',
  noticeText: 'text-sm text-sky-800',
  tabs: 'mb-6 flex border-b border-gray-200',
  tab: {
    base: 'flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors duration-200 rounded-t-md',
    practice: 'ml-2',
    active: 'border-gray-700 text-gray-700',
    inactive: 'border-transparent text-gray-400 hover:text-gray-700',
  },
  tabIcon: 'w-4 h-4',
  toolbar: 'flex items-center justify-between mb-4',
  toolbarLeft: 'flex gap-4 items-center',
  pager: 'flex gap-2',
  pagerIcon: 'w-4 h-4',
  count: 'text-sm text-gray-600',
  filterTrigger: 'w-[200px] h-9 text-sm bg-white',
  empty: 'text-center py-12',
  emptyIconWrap: 'w-16 h-16 bg-cyan-900/10 rounded-full flex items-center justify-center mx-auto mb-4',
  emptyIcon: 'w-8 h-8 text-cyan-900',
  emptyTitle: 'text-xl font-medium text-gray-700 mb-2',
  emptyText: 'text-gray-500',
} as const;

export function reportsTabClass(isActive: boolean, isPracticeTab = false): string {
  return cn(
    reportsPage.tab.base,
    isPracticeTab && reportsPage.tab.practice,
    isActive ? reportsPage.tab.active : reportsPage.tab.inactive
  );
}

export const proctorDashboard = {
  shell: 'min-h-screen bg-aralin-bg',
  inner: 'max-w-6xl mx-auto px-4 pb-8 space-y-8',
  header: 'pt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between',
  title: 'text-xl font-semibold text-gray-900',
  subtitle: 'text-sm text-gray-700',
  examSelectWrap: 'w-full sm:w-64',
  examTrigger: 'bg-white',
  stats: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
  statCard: 'rounded-xl bg-white shadow-sm border border-gray-100 px-4 py-3',
  statLabel: 'text-xs uppercase tracking-wide text-gray-500',
  statValue: 'mt-1 text-2xl font-semibold text-gray-900',
  statValueOngoing: 'mt-1 text-2xl font-semibold text-yellow-500',
  statValueCompleted: 'mt-1 text-2xl font-semibold text-green-600',
  legend: 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3',
  legendRow: 'flex items-center gap-2 text-xs text-gray-500',
  legendItem: 'flex items-center gap-1',
  legendDotNoSession: 'h-2 w-2 rounded-full bg-red-500',
  legendDotOngoing: 'h-2 w-2 rounded-full bg-yellow-400',
  legendDotCompleted: 'h-2 w-2 rounded-full bg-green-500',
  tableCard: 'bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden',
  tableHeader: 'px-4 py-3 border-b border-gray-100 space-y-3',
  tableHeaderRow: 'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between',
  tableTitle: 'text-sm font-medium text-gray-900',
  searchWrap: 'w-full sm:w-64',
  searchInput: 'h-8 text-sm',
  toolbar: 'flex flex-wrap items-center gap-3 justify-between',
  sortRow: 'flex items-center gap-2 text-xs text-gray-500',
  sortTrigger: 'h-8 w-28 bg-white',
  toolbarRight: 'flex items-center gap-2',
  sortDirButton:
    'text-xs text-gray-600 border border-gray-200 rounded-md px-2 py-1 hover:bg-gray-50',
  loadingText: 'text-xs text-gray-500',
  error: 'px-4 py-3 text-sm text-red-600 border-b border-gray-100',
  tableWrap: 'overflow-x-auto',
  table: 'min-w-full text-sm',
  thead: 'bg-gray-50',
  th: 'px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
  tbody: 'divide-y divide-gray-100',
  emptyCell: 'px-4 py-6 text-center text-sm text-gray-500',
  td: 'px-4 py-2 whitespace-nowrap',
  tdMuted: 'px-4 py-2 whitespace-nowrap text-gray-700',
  tdStatus: 'px-4 py-2 whitespace-nowrap text-gray-700 capitalize',
  studentCell: 'flex items-center gap-2',
  statusDot: {
    base: 'h-2.5 w-2.5 rounded-full',
    ongoing: 'bg-yellow-400',
    completed: 'bg-green-500',
    none: 'bg-red-500',
  },
  studentName: 'text-gray-900',
  pagination:
    'px-4 py-3 border-t border-gray-100 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-xs text-gray-600',
  paginationStrong: 'font-medium',
  paginationControls: 'flex items-center gap-2',
  pageButton:
    'px-2 py-1 rounded-md border border-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 text-xs',
} as const;

export function proctorDashboardStatusDotClass(status: string): string {
  const normalized = status.toLowerCase();
  return cn(
    proctorDashboard.statusDot.base,
    normalized === 'ongoing' && proctorDashboard.statusDot.ongoing,
    (normalized === 'completed' || normalized === 'time_expired') &&
      proctorDashboard.statusDot.completed,
    normalized !== 'ongoing' &&
      normalized !== 'completed' &&
      normalized !== 'time_expired' &&
      proctorDashboard.statusDot.none
  );
}

export const landingPage = {
  shell: 'min-h-screen flex flex-col bg-white px-4 overflow-x-hidden',
  header: 'flex flex-col sm:flex-row justify-between items-center px-6 py-4 sm:pr-32 w-full gap-4',
  loginButton: 'bg-aralin-green hover:bg-aralin-green-dark text-white px-6 py-2 rounded-4xl',
  main: 'flex-1 text-center mt-10 px-4',
  tagline:
    'text-lg sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#2A4F6E] to-[#44AA88] mb-2',
  headingWrap: 'relative inline-block mt-4',
  graphicLeft: 'absolute -top-8 left-4 w-10 sm:w-16 hidden md:block',
  graphicRight: 'absolute top-20 -right-20 w-10 sm:w-16 hidden md:block',
  heading: 'text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-4',
  headingLine:
    'text-transparent bg-clip-text bg-gradient-to-r from-[#44AA88] to-[#2A4F6E] block',
  description:
    'text-base sm:text-xl font-light text-aralin-green max-w-xl mx-auto mb-10 leading-snug text-center px-4',
  signupButton:
    'bg-gradient-to-r from-[#44AA88] to-[#2A4F6E] hover:from-[#2A4F6E] hover:to-[#44AA88] text-white px-8 py-3 rounded-3xl text-base sm:text-lg font-semibold transition duration-300',
  bottomGraphicWrap: 'flex justify-center mt-8',
  bottomGraphic: 'w-full max-w-[90vw] sm:max-w-[80vw] md:max-w-[70vw] lg:max-w-[1000px] mx-auto',
  footer: 'py-6 bg-white border-t text-center text-sm text-gray-500',
  footerCopy: 'mb-2',
  brand: 'font-semibold text-aralin-green',
  footerLink: 'hover:underline cursor-pointer',
} as const;
